import { TypedDocumentNode, gql } from '@apollo/client';

// eslint-disable-next-line no-restricted-imports
import type { RewardBox } from '@sorare/trois/src/components/ClaimRewardBoxes/type';
import {
  ProbabilisticBundleDesign,
  ProbabilisticBundleIntensity,
  ProbabilisticBundleQuality,
  Rarity,
} from '__generated__/globalTypes';
import { withFragments } from 'lib/gql';

import type {
  getBoxColor_ProbabilisticBundle,
  getRewardBoxRepresentation_ProbabilisticBundle,
} from './__generated__/probabilisticBundle.graphql';
import { type FragmentOf, unmaskFragment } from './graphql';

export const getBoxColor = withFragments(
  (box: FragmentOf<getBoxColor_ProbabilisticBundle>) => {
    if (box.config.design !== ProbabilisticBundleDesign.LOYALTY) {
      return {
        [ProbabilisticBundleQuality.LITE]: '#999999',
        [ProbabilisticBundleQuality.STANDARD]: '#444444',
        [ProbabilisticBundleQuality.PREMIUM]: '#090909',
      }[box.config.quality];
    }
    return {
      [Rarity.common]: '#999999',
      [Rarity.custom_series]: '#999999',
      [Rarity.limited]: '#693C3C', //bronze
      [Rarity.rare]: '#B1B1B1', //silver
      [Rarity.super_rare]: '#FFbb22', //gold
      [Rarity.unique]: '#50C3E0', //diamond
    }[box.config.rarity];
  },
  {
    ProbabilisticBundle: gql`
      fragment getBoxColor_ProbabilisticBundle on ProbabilisticBundle {
        id
        config {
          id
          quality
          design
          rarity
        }
      }
    ` as TypedDocumentNode<getBoxColor_ProbabilisticBundle>,
  }
);

export const BOX_LIGHT_INTENSITY: {
  [k in ProbabilisticBundleIntensity]: number;
} = {
  [ProbabilisticBundleIntensity.DEFAULT]: 1,
  [ProbabilisticBundleIntensity.MODERATE]: 2,
  [ProbabilisticBundleIntensity.BRIGHT]: 4,
  [ProbabilisticBundleIntensity.HIGH]: 6,
  [ProbabilisticBundleIntensity.INTENSE]: 10,
};

export const getRewardBoxRepresentation = withFragments(
  (
    box_: FragmentOf<getRewardBoxRepresentation_ProbabilisticBundle>
  ): RewardBox => {
    const box = unmaskFragment(box_);
    return {
      id: box.id,
      type: box.config.design.toString(),
      boxColor: getBoxColor(box),
      frameColor: getBoxColor(box),
      neonColor: box.config.neonColor,
      rewardColor: box.items.at(0)?.neonColor || '',
      intensity: BOX_LIGHT_INTENSITY[box.items.at(0)?.intensity || 'DEFAULT'],
      division: box.config.division ?? undefined,
      stars: box.config.stars ?? undefined,
      opened: box.opened,
      isOpenable: box.isOpenable,
      openableAt: box.openableAt,
      children: null,
      onClaim: async () => {},
      fileName: 'Reward',
    };
  },
  {
    ProbabilisticBundle: gql`
      fragment getRewardBoxRepresentation_ProbabilisticBundle on ProbabilisticBundle {
        id
        opened
        config {
          id
          name
          pictureUrl
          division
          design
          neonColor
          stars
        }
        isOpenable
        openableAt
        items {
          id
          neonColor
          intensity
        }
        ...getBoxColor_ProbabilisticBundle
      }
      ${getBoxColor.fragments.ProbabilisticBundle}
    ` as TypedDocumentNode<getRewardBoxRepresentation_ProbabilisticBundle>,
  }
);
